import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
    Bell,
    BellOff,
    ChevronDown,
    LoaderCircle,
    LucideAngularModule,
    PhoneCall,
    PhoneIncoming,
    X,
} from 'lucide-angular';

import { ContactAvatarComponent } from '@shared/components/contact-avatar/contact-avatar.component';
import { ContactChannelIconsComponent } from '@shared/components/contact-channel-icons/contact-channel-icons.component';
import { VOICE_CHANNELS, VOICE_TYPE } from '@shared/types/channels.types';

import { VoiceCallService } from '../../services/voice-call.service';
import { VoiceShellService, VoiceWaitingCall } from '../../services/voice-shell.service';

/**
 * Floating "calls waiting" widget mounted at the desktop root, next to the call bar.
 * Collapsed it's a pill with a live count; expanded it lists every conversation in
 * WAITING_AGENT (mine first, then FIFO) with a live wait timer and a direct "pick up"
 * per row. The list, the dismissals, the sound preference and the cues all live in
 * {@link VoiceShellService} — the incoming-call card reads the same state, so the two
 * surfaces never disagree.
 */
@Component({
    selector: 'app-voice-queue-widget',
    standalone: true,
    imports: [
        TranslatePipe,
        LucideAngularModule,
        ContactAvatarComponent,
        ContactChannelIconsComponent,
    ],
    templateUrl: './voice-queue-widget.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'contents' },
    styles: [
        `
            @keyframes queue-ring {
                0%,
                12%,
                100% {
                    transform: rotate(0deg);
                }
                2%,
                6% {
                    transform: rotate(-12deg);
                }
                4%,
                8% {
                    transform: rotate(12deg);
                }
            }
            .queue-ring-icon {
                animation: queue-ring 3.2s ease-in-out infinite;
                transform-origin: 50% 40%;
            }
        `,
    ],
})
export class VoiceQueueWidgetComponent {
    protected readonly shell = inject(VoiceShellService);
    private readonly voiceCallS = inject(VoiceCallService);

    protected readonly PhoneIncomingIcon = PhoneIncoming;
    protected readonly PhoneCallIcon = PhoneCall;
    protected readonly LoaderIcon = LoaderCircle;
    protected readonly ChevronDownIcon = ChevronDown;
    protected readonly BellIcon = Bell;
    protected readonly BellOffIcon = BellOff;
    protected readonly XIcon = X;
    /** KAN-232: a WhatsApp call is a VOICE-channel call whose voice_type is WHATSAPP. */
    protected readonly VOICE_CHANNEL = VOICE_CHANNELS.voice;
    protected readonly WA_TYPE = VOICE_TYPE.whatsapp;

    protected readonly expanded = this.shell.queueExpanded;
    protected readonly soundOn = this.shell.soundOn;
    protected readonly waiting = this.shell.waiting;
    protected readonly count = this.shell.count;
    protected readonly hasCallForMe = this.shell.hasCallForMe;
    protected readonly onCall = this.shell.onCall;
    protected readonly registered = this.shell.registered;

    /** Shift up when the active-call bar occupies the bottom-right corner. */
    protected readonly stackAboveCallBar = computed(() => this.voiceCallS.context() !== null);

    protected isPickupReady(entry: VoiceWaitingCall): boolean {
        return this.shell.isPickupReady(entry);
    }

    protected isPickingUp(entry: VoiceWaitingCall): boolean {
        return this.shell.isPickingUp(entry);
    }

    protected waitSecOf(entry: VoiceWaitingCall): number {
        return this.shell.waitSecOf(entry);
    }

    protected dismiss(entry: VoiceWaitingCall) {
        this.shell.dismiss(entry);
    }

    protected pickup(entry: VoiceWaitingCall) {
        this.shell.answer(entry);
    }

    protected toggleExpanded() {
        this.shell.toggleQueue();
    }

    protected toggleSound() {
        this.shell.toggleSound();
    }

    protected waitLabel(sec: number): string {
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${m}:${s.toString().padStart(2, '0')}`;
    }

    /** Wait-time urgency: calm < 30s, warning < 90s, alarming beyond. */
    protected slaClass(sec: number): string {
        if (sec < 30) return 'text-emerald-600';
        if (sec < 90) return 'text-amber-600';
        return 'text-red-600';
    }

    protected slaDotClass(sec: number): string {
        if (sec < 30) return 'bg-emerald-500';
        if (sec < 90) return 'bg-amber-500';
        return 'bg-red-500';
    }
}
